import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './SignaturePad.module.css';
import { SignatureLibraryModal } from './SignatureLibrary';
import { cleanSignature, fileToCanvas, imageAspect, toRealSignature } from '../lib/signatureImage';

/**
 * Canvas podepisovací pole — prst / myš / touch + upload obrázku (PNG/JPG/PDF export jako obr).
 * Port chování z frontend/index.html:13959 (sigInit + sigClear + sigLoadImage).
 *
 * Controlled: value = dataURL PNG (nebo ''), onChange se volá:
 *  – po skončení tahu (mouseup/touchend),
 *  – po vymazání,
 *  – po načtení obrázku.
 *
 * Režim „zvětšit": tlačítko otevře fullscreen překrývku s velkým plátnem
 * pro pohodlné ruční podepsání na tabletu / displeji notebooku.
 */

export interface SignaturePadProps {
  value?: string;
  onChange?: (dataUrl: string) => void;
  width?: number;
  height?: number;
  hint?: string;
  disabled?: boolean;
  /** Skrýt tlačítko „Uložený" (uvnitř samotné knihovny podpisů). */
  hideLibrary?: boolean;
}

const DEFAULT_W = 400;
const DEFAULT_H = 110;

/** Výchozí buffer pro zvětšené plátno (CSS se škáluje podle viewportu). */
const BIG_W = 1600;
const BIG_H = 440;

/** Násobek rozlišení inline plátna vůči width/height. */
const BUF_SCALE = 2;

interface SigCanvasProps {
  value?: string;
  onChange?: (dataUrl: string) => void;
  width: number;
  height: number;
  disabled?: boolean;
  className?: string;
  hint?: string;
  /**
   * Responzivní buffer: rozlišení plátna se odvodí ze skutečné velikosti boxu
   * (× DPR), místo pevných width/height. Box si přitom drží stejný poměr stran
   * jako výstup (viz CSS aspect-ratio), takže kreslení je 1:1 nezkreslené a
   * uložený PNG sedí do landscape podpisového řádku v dokumentu/PDF.
   */
  responsive?: boolean;
  /**
   * Dotykem se nekreslí — prst přes plátno posouvá stránku a klepnutí zavolá
   * onTap (otevře zvětšené plátno). Myš kreslí dál normálně. Inline pole
   * s `touch-action: none` jinak na telefonu blokují scroll dokumentu.
   */
  onTap?: () => void;
}

/** Vnitřní kreslicí plátno — sdíleno mezi inline a fullscreen režimem. */
function SigCanvas({ value, onChange, width, height, disabled, className, hint, responsive, onTap }: SigCanvasProps) {
  const tapStartRef = useRef<{ x: number; y: number } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const drawingRef = useRef(false);
  const lastRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState<boolean>(!!value);
  // Inline plátno má buffer 2× větší než logická velikost — podpis/razítko
  // v PDF (kde je pole větší než na obrazovce) jinak vychází rozmazaný.
  const [dims, setDims] = useState<{ w: number; h: number }>({ w: width * BUF_SCALE, h: height * BUF_SCALE });

  // Responzivní režim: změř box a nastav buffer na jeho px velikost × DPR.
  useEffect(() => {
    if (!responsive) {
      const w = width * BUF_SCALE;
      const h = height * BUF_SCALE;
      setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
      return;
    }
    const box = boxRef.current;
    if (!box) return;
    const update = () => {
      const r = box.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(r.width * dpr));
      const h = Math.max(1, Math.round(r.height * dpr));
      setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(box);
    return () => ro.disconnect();
  }, [responsive, width, height]);

  const cw = dims.w;
  const ch = dims.h;

  const pos = useCallback((e: PointerEvent | TouchEvent | MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const r = canvas.getBoundingClientRect();
    const sx = canvas.width / r.width;
    const sy = canvas.height / r.height;
    let clientX = 0;
    let clientY = 0;
    if ('touches' in e && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if ('clientX' in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    return { x: (clientX - r.left) * sx, y: (clientY - r.top) * sy };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!value) {
      setHasInk(false);
      return;
    }
    const img = new Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // Vepsat se zachováním poměru stran (vložený podpis může mít jiný poměr).
      const k = Math.min(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
      const dw = img.naturalWidth * k;
      const dh = img.naturalHeight * k;
      ctx.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
      setHasInk(true);
    };
    img.src = value;
    // cw/ch v závislostech: po responzivní změně rozlišení plátno překreslíme.
  }, [value, cw, ch]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Tloušťku přizpůsobíme velikosti bufferu, aby byl tah vizuálně konzistentní.
    ctx.strokeStyle = '#12325a';
    ctx.lineWidth = Math.max(2.2, cw / 180);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [cw]);

  function emitChange() {
    const canvas = canvasRef.current;
    if (!canvas || !onChange) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let any = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) {
        any = true;
        break;
      }
    }
    onChange(any ? canvas.toDataURL('image/png') : '');
    setHasInk(any);
  }

  function start(e: React.MouseEvent | React.TouchEvent) {
    if (disabled) return;
    drawingRef.current = true;
    const p = pos(e.nativeEvent as MouseEvent | TouchEvent);
    lastRef.current = p;
    e.preventDefault();
  }

  function move(e: React.MouseEvent | React.TouchEvent) {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const p = pos(e.nativeEvent as MouseEvent | TouchEvent);
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    setHasInk(true);
    e.preventDefault();
  }

  function end() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    emitChange();
  }

  function tapStart(e: React.TouchEvent) {
    const t = e.touches[0];
    tapStartRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  }

  function tapEnd(e: React.TouchEvent) {
    const s = tapStartRef.current;
    tapStartRef.current = null;
    const t = e.changedTouches[0];
    if (!s || !t || disabled) return;
    // Posun prstem = scroll stránky, ne klepnutí.
    if (Math.abs(t.clientX - s.x) > 10 || Math.abs(t.clientY - s.y) > 10) return;
    e.preventDefault(); // potlačí následný emulovaný mousedown/click
    onTap?.();
  }

  return (
    <div
      className={`${className ?? styles.canvasBox}${onTap ? ` ${styles.tapOnly}` : ''}`}
      ref={boxRef}
    >
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        width={cw}
        height={ch}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={onTap ? tapStart : start}
        onTouchMove={onTap ? undefined : move}
        onTouchEnd={onTap ? tapEnd : end}
      />
      {!hasInk && hint && <div className={styles.placeholder}>{hint}</div>}
    </div>
  );
}

export function SignaturePad({
  value,
  onChange,
  width = DEFAULT_W,
  height = DEFAULT_H,
  hint = 'Podepsat prstem / myší',
  disabled,
  hideLibrary,
}: SignaturePadProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  // Dočasná hodnota v editoru „na celou obrazovku" — aby šlo zrušit bez ztráty originálu.
  const [bigValue, setBigValue] = useState<string>(value ?? '');

  useEffect(() => {
    if (expanded) setBigValue(value ?? '');
  }, [expanded, value]);

  useEffect(() => {
    if (!expanded) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  function handleClear() {
    onChange?.('');
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadError('');
    try {
      // Sken / fotka podpisu (i PDF): odstraní bílé pozadí a ořízne, pak vycentruje do pole.
      const png = cleanSignature(await fileToCanvas(file), { removeBackground: true });
      if (!png) {
        setUploadError('V souboru není žádný podpis.');
        return;
      }
      onChange?.(await toRealSignature(png, width / height));
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Soubor se nepodařilo načíst.');
    }
  }

  async function handlePick(dataUrl: string) {
    try {
      onChange?.(await toRealSignature(dataUrl, width / height));
    } catch {
      setUploadError('Podpis se nepodařilo vložit.');
    }
  }

  const hasInk = !!value;

  // Vložený podpis (knihovna / soubor) má jiný poměr stran než kreslicí pole →
  // zobrazí se ve skutečné velikosti a pole se mu přizpůsobí (jako v Podepsat).
  const [isReal, setIsReal] = useState(false);
  useEffect(() => {
    let alive = true;
    if (!value) {
      setIsReal(false);
      return;
    }
    const pad = width / height;
    void imageAspect(value).then((a) => {
      if (alive) setIsReal(a > 0 && Math.abs(a - pad) / pad > 0.04);
    });
    return () => {
      alive = false;
    };
  }, [value, width, height]);
  // Dotykové zařízení (třída z main.tsx): inline plátno jen jako náhled, podpis ve zvětšeném.
  const isTouch = typeof document !== 'undefined' && document.documentElement.classList.contains('touch');

  return (
    <div className={styles.wrap}>
      {isReal && value ? (
        <div className={`${styles.canvasBox} ${styles.realBox}`}>
          <img src={value} alt="Podpis" className={styles.realImg} data-sig-real="1" />
        </div>
      ) : (
        <SigCanvas
          value={value}
          onChange={onChange}
          width={width}
          height={height}
          disabled={disabled}
          hint={isTouch ? 'Klepnutím podepsat' : hint}
          onTap={isTouch ? () => setExpanded(true) : undefined}
        />
      )}
      <div className={styles.controls}>
        <span className={styles.hint}>
          {uploadError ? (
            <span className={styles.err}>{uploadError}</span>
          ) : isReal ? (
            'Vložený podpis — pro ruční podpis ho nejdřív vymaž.'
          ) : hasInk ? (
            'Podpis uložen.'
          ) : (
            hint
          )}
        </span>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf,.pdf"
          className={styles.fileInput}
          onChange={handleUpload}
        />
        <button
          type="button"
          className={styles.btn}
          onClick={() => setExpanded(true)}
          disabled={disabled}
          title="Zvětšit pro podpis na tabletu / telefonu"
        >
          ⛶ Zvětšit
        </button>
        {!hideLibrary && (
          <button
            type="button"
            className={styles.btn}
            onClick={() => setLibraryOpen(true)}
            disabled={disabled}
            title="Vložit podpis z knihovny podpisů"
          >
            📚 Uložený
          </button>
        )}
        <button
          type="button"
          className={styles.btn}
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          title="Načíst podpis ze souboru (PNG, JPG, PDF) — bílé pozadí se odstraní"
        >
          📎 Nahrát
        </button>
        <button
          type="button"
          className={styles.btn}
          onClick={handleClear}
          disabled={disabled || !hasInk}
          title="Vymazat podpis"
        >
          ✕ Vymazat
        </button>
      </div>

      {!hideLibrary && (
        <SignatureLibraryModal
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          onPick={(p) => void handlePick(p.data_url)}
        />
      )}

      {expanded &&
        createPortal(
          <div
            className={styles.fsOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setExpanded(false);
            }}
          >
            <div className={styles.fsDialog} role="dialog" aria-modal="true" aria-label="Zvětšený podpis">
              <div className={styles.fsHeader}>
                <div className={styles.fsTitle}>Podpis — zvětšené plátno</div>
                <button
                  type="button"
                  className={styles.fsClose}
                  onClick={() => setExpanded(false)}
                  aria-label="Zavřít bez uložení"
                >
                  ×
                </button>
              </div>
              <div className={styles.fsBody}>
                <SigCanvas
                  value={bigValue}
                  onChange={setBigValue}
                  width={BIG_W}
                  height={BIG_H}
                  responsive
                  disabled={disabled}
                  className={styles.fsCanvasBox}
                  hint="Podepište se prstem nebo stylusem"
                />
              </div>
              <div className={styles.fsFooter}>
                <button
                  type="button"
                  className={styles.fsBtn}
                  onClick={() => setBigValue('')}
                  disabled={disabled || !bigValue}
                >
                  ✕ Vymazat
                </button>
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  className={styles.fsBtn}
                  onClick={() => setExpanded(false)}
                >
                  Zrušit
                </button>
                <button
                  type="button"
                  className={`${styles.fsBtn} ${styles.fsBtnPrimary}`}
                  onClick={() => {
                    onChange?.(bigValue);
                    setExpanded(false);
                  }}
                  disabled={disabled}
                >
                  ✓ Uložit podpis
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
